import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculateDistanceMiles } from '@/lib/distanceUtils';

export interface GameStoreSocials {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  tiktok?: string;
}

export interface GameStore {
  id: string;
  source: string;
  osm_id: string | null;
  name: string;
  address_line1: string | null;
  city: string | null;
  state_code: string | null;
  postal_code: string | null;
  country: string | null;
  lat: number;
  lng: number;
  phone: string | null;
  website: string | null;
  email: string | null;
  description: string | null;
  brand: string | null;
  opening_hours: string | null;
  socials: GameStoreSocials | null;
  tags: string[] | null;
  county: string | null;
  claim_status: 'unclaimed' | 'pending_claim' | 'claimed';
  claimed_by_user_id: string | null;
}

/**
 * Lean fetch for the map: only the fields a pin or list row needs.
 */
export function useGameStores(enabled = true) {
  return useQuery({
    queryKey: ['game-stores', 'pins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('game_stores')
        .select('id, name, lat, lng, claim_status, source, city, state_code')
        .limit(2000);
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        source: r.source ?? 'osm',
        osm_id: null,
        name: r.name,
        address_line1: null,
        city: r.city ?? null,
        state_code: r.state_code ?? null,
        postal_code: null,
        country: null,
        lat: Number(r.lat),
        lng: Number(r.lng),
        phone: null,
        website: null,
        email: null,
        description: null,
        brand: null,
        opening_hours: null,
        socials: null,
        tags: null,
        county: null,
        claim_status: r.claim_status as GameStore['claim_status'],
        claimed_by_user_id: null,
      })) as GameStore[];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export interface GameStoreWithDistance extends GameStore {
  distance_miles: number | null;
}

/**
 * Stores augmented with distance from a user location, sorted nearest first
 * and optionally filtered by radius.
 */
export function useGameStoresNearby(
  userLocation: { lat: number; lng: number } | null,
  radiusMiles: number | null,
  enabled = true,
) {
  const { data: stores = [], ...rest } = useGameStores(enabled);

  const nearby = useMemo<GameStoreWithDistance[]>(() => {
    const withDist: GameStoreWithDistance[] = stores.map((s) => ({
      ...s,
      distance_miles: userLocation
        ? calculateDistanceMiles(userLocation.lat, userLocation.lng, s.lat, s.lng)
        : null,
    }));
    let filtered = withDist;
    if (userLocation && radiusMiles != null) {
      filtered = withDist.filter(
        (s) => s.distance_miles != null && s.distance_miles <= radiusMiles,
      );
    }
    filtered.sort((a, b) => {
      if (a.distance_miles == null && b.distance_miles == null) return a.name.localeCompare(b.name);
      if (a.distance_miles == null) return 1;
      if (b.distance_miles == null) return -1;
      return a.distance_miles - b.distance_miles;
    });
    return filtered;
  }, [stores, userLocation, radiusMiles]);

  return { ...rest, data: nearby, allStores: stores };
}

/** Full record (address, website, hours, socials, etc.) for the popup/sheet detail view. */
export function useGameStoreDetail(id: string | null) {
  return useQuery({
    queryKey: ['game-store-detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('game_stores')
        .select(
          'id, source, osm_id, name, address_line1, city, state_code, postal_code, country, lat, lng, phone, website, description, brand, opening_hours, socials, tags, county, claim_status, claimed_by_user_id',
        )
        .eq('id', id!)
        .single();
      if (error) throw error;
      return {
        ...data,
        email: null,
        lat: Number(data.lat),
        lng: Number(data.lng),
        socials: (data.socials ?? null) as GameStoreSocials | null,
      } as GameStore;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}
